import styled from "styled-components";

import { ACCENT, ACCENT_TINT } from "./accent";

/** How far the lines other than the selected one fade while a line of their part is selected. */
export const DIMMED = 0.45;

/**
 * One part of the breakdown card: a title line holding the part's total, and a list of lines, each a name, its rows, a bar
 * and its loss with its share. On a phone the bar takes a line of its own under the figures.
 */
export const Part = styled.div`
    padding: 12px 20px 10px;
    min-width: 0;
    @media (max-width: 600px) { padding: 12px 16px 10px; }
`;

export const PartHead = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: 8px;
    row-gap: 6px;
`;

export const PartTitle = styled.h3`
    margin: 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: #0f1419;
`;

export const PartTotal = styled.span`
    font-size: 0.75rem;
    font-variant-numeric: tabular-nums;
    color: #5a6473;
`;

export const List = styled.ul`
    margin: 8px 0 0;
    padding: 0;
    list-style: none;
`;

const columns = `
    display: grid;
    grid-template-columns: minmax(0, 150px) 48px minmax(0, 1fr) auto;
    grid-template-areas: "name count bar figures";
    align-items: center;
    column-gap: 12px;
    > :nth-child(1) { grid-area: name; }
    > :nth-child(2) { grid-area: count; }
    > :nth-child(3) { grid-area: bar; }
    > :nth-child(4) { grid-area: figures; }
    @media (max-width: 600px) {
        grid-template-columns: minmax(0, 1fr) 48px auto;
        grid-template-areas: "name count figures" "bar bar bar";
    }
`;

export const Head = styled.li`
    ${columns}
    padding: 0 0 4px;
    font-size: 0.7rem;
    color: #8b94a3;
    > :nth-child(4) { justify-self: end; width: max-content; }
    @media (max-width: 600px) { > :nth-child(3) { display: none; } > :nth-child(4) { width: auto; } }
`;

export const Item = styled.button`
    ${columns}
    width: calc(100% + 16px);
    margin: 0 -8px;
    padding: 4px 8px;
    border: none;
    border-radius: 6px;
    background: ${({ $selected }) => ($selected ? ACCENT_TINT : "none")};
    box-shadow: ${({ $selected }) => ($selected ? `inset 3px 0 0 ${ACCENT}` : "none")};
    opacity: ${({ $dimmed }) => ($dimmed ? DIMMED : 1)};
    font: inherit;
    text-align: left;
    cursor: pointer;
    > :nth-child(1) { font-weight: ${({ $selected }) => ($selected ? 600 : 400)}; }
    &:hover { background: ${({ $selected }) => ($selected ? ACCENT_TINT : "#f5f6f8")}; opacity: 1; }
    @media (max-width: 600px) { row-gap: 4px; }
`;

export const Name = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 0.8rem;
    color: #0f1419;
`;

export const Count = styled.span`
    text-align: right;
    font-size: 0.75rem;
    font-variant-numeric: tabular-nums;
    color: #5a6473;
`;

export const Track = styled.span`
    height: 8px;
    overflow: hidden;
    border-radius: 999px;
    background: #e4e7ecb3;
`;

export const Fill = styled.span`
    display: block;
    height: 100%;
    border-radius: 999px;
    background: #d92d20;
`;

/** A column per measure, then the share. */
export const Figures = styled.span`
    display: grid;
    grid-template-columns: repeat(${({ $measures }) => $measures}, 68px) 36px;
    column-gap: 6px;
    text-align: right;
    font-size: 0.75rem;
    font-variant-numeric: tabular-nums;
    strong { font-weight: 500; color: #0f1419; }
    > :last-child { color: #5a6473; }
`;

export const compact = new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 });

export const shareOf = (impact, totalImpact) => (totalImpact > 0 ? `${Math.round((impact / totalImpact) * 100)}%` : "–");

/** The bar's width: its share of the longest bar, never thinner than a sliver when there is an impact. */
export const barWidthOf = (impact, maxImpact) => `${Math.max(impact > 0 ? 2 : 0, (impact / maxImpact) * 100)}%`;

/** A loss a month as written: "~4.9k", or "0". */
export const amountOf = (lostPerMonth) => (Math.round(lostPerMonth) > 0 ? `~${compact.format(Math.round(lostPerMonth))}` : "0");

/** The loss a month on each of `measures`: "~4.9k impressions · ~120 sessions". */
export const lossesOf = (lostPerMonth, measures) => measures.map(({ key, noun }) => `${amountOf(lostPerMonth[key])} ${noun}`).join(" · ");
